import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  completeProductionOrder,
  completeProductionOrderOperation,
  createProductionOrderOperationReport,
  createProductionOrderReport,
  finishProductionOrderOperation,
  getProductionOrder,
  startProductionOrderOperation,
} from "../../api/productionOrders";

export default function OperatorOrderReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const orderId = Number(id);

  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const [opStartDataByRoutingOpId, setOpStartDataByRoutingOpId] = useState({});
  const [finishPanel, setFinishPanel] = useState(null); // { rid, poOp, qtyDone, requirements, availableLots }
  const [consumptionInputs, setConsumptionInputs] = useState({}); // key: mat:var:lot -> string
  const [manualConsumptionsByReqKey, setManualConsumptionsByReqKey] = useState({}); // key: mat:var -> [{id, lot_no, qty}]
  const [outputLotByRoutingOpId, setOutputLotByRoutingOpId] = useState({});

  const [qtyGood, setQtyGood] = useState(0);
  const [qtyScrap, setQtyScrap] = useState(0);
  const [note, setNote] = useState("");

  const [selectedRoutingOperationId, setSelectedRoutingOperationId] = useState("");
  const [opSetupMin, setOpSetupMin] = useState(0);
  const [opMachineMin, setOpMachineMin] = useState(0);
  const [opLaborMin, setOpLaborMin] = useState(0);
  const [opQtyGood, setOpQtyGood] = useState(0);
  const [opQtyScrap, setOpQtyScrap] = useState(0);
  const [opNote, setOpNote] = useState("");

  const [qtyDoneByRoutingOpId, setQtyDoneByRoutingOpId] = useState({});
  const [opActionError, setOpActionError] = useState("");

  function buildLotKey({ material_id, material_variant_id, lot_no }) {
    return `${Number(material_id)}:${material_variant_id ? Number(material_variant_id) : 0}:${String(lot_no || "")}`;
  }

  function buildReqKey({ material_id, material_variant_id }) {
    return `${Number(material_id)}:${material_variant_id ? Number(material_variant_id) : 0}`;
  }

  function normalizeLotNo(v) {
    return String(v || "").trim();
  }

  function mergeConsumptions(rows) {
    const map = new Map(); // lotKey -> row
    for (const r of rows || []) {
      const matId = Number(r?.material_id);
      const varId =
        r?.material_variant_id === null || r?.material_variant_id === undefined ? null : Number(r.material_variant_id);
      const lotNo = normalizeLotNo(r?.lot_no);
      const qty = Number(r?.qty);
      if (!Number.isFinite(matId) || !Number.isFinite(qty) || qty <= 0 || !lotNo) continue;
      const k = buildLotKey({ material_id: matId, material_variant_id: varId, lot_no: lotNo });
      const cur = map.get(k) || { material_id: matId, material_variant_id: varId, lot_no: lotNo, qty: 0 };
      cur.qty = Number(cur.qty) + qty;
      map.set(k, cur);
    }
    return Array.from(map.values());
  }

  function load() {
    if (!Number.isFinite(orderId)) return;
    setLoadingOrder(true);
    getProductionOrder(orderId)
      .then((data) => {
        setOrder(data || null);
        const planned = Number(data?.planned_qty || 0);
        setQtyGood(planned > 0 ? planned : 0);
        setQtyScrap(0);
        setNote("");

        const poOps = Array.isArray(data?.production_order_operations) ? data.production_order_operations : [];
        const map = {};
        for (const x of poOps) {
          const rid = Number(x.routing_operation_id);
          if (!Number.isFinite(rid)) continue;
          const target = Number(x.qty_target || planned || 0);
          map[rid] = Number.isFinite(target) && target > 0 ? target : 0;
        }
        setQtyDoneByRoutingOpId(map);
        setOpActionError("");

        const ops = Array.isArray(data?.routing_operations) ? data.routing_operations : [];
        setSelectedRoutingOperationId(ops[0]?.id ? String(ops[0].id) : "");
        setOpSetupMin(0);
        setOpMachineMin(0);
        setOpLaborMin(0);
        setOpQtyGood(0);
        setOpQtyScrap(0);
        setOpNote("");
      })
      .catch((err) => {
        console.error("ORDER LOAD ERROR", err);
        alert("İş emri yüklenemedi");
      })
      .finally(() => setLoadingOrder(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  async function submitReport() {
    if (!Number.isFinite(orderId)) return;
    const qg = Number(qtyGood);
    const qs = Number(qtyScrap);
    if (!Number.isFinite(qg) || qg < 0) {
      alert("İyi miktar (qty_good) >= 0 olmalı");
      return;
    }
    if (!Number.isFinite(qs) || qs < 0) {
      alert("Fire (qty_scrap) >= 0 olmalı");
      return;
    }
    if (qg + qs <= 0) {
      alert("İyi + fire toplamı 0 olamaz");
      return;
    }

    try {
      await createProductionOrderReport(orderId, { qty_good: qg, qty_scrap: qs, note: note || null });
      alert("Rapor kaydedildi.");
      await load();
    } catch (err) {
      console.error("REPORT SUBMIT ERROR", err);
      alert("Rapor gönderilemedi");
    }
  }

  async function startOp(poOp) {
    setOpActionError("");
    try {
      const rid = Number(poOp.routing_operation_id);
      const data = await startProductionOrderOperation(orderId, rid);
      setOpStartDataByRoutingOpId((prev) => ({ ...prev, [rid]: data }));
      await load();
    } catch (err) {
      console.error("OP START ERROR", err);
      const msg = err?.response?.data?.error?.message || err?.message || "Operasyon başlatılamadı";
      setOpActionError(msg);
      alert(msg);
    }
  }

  async function openFinishPanel(poOp) {
    setOpActionError("");
    const rid = Number(poOp.routing_operation_id);
    const qtyDone = Number(qtyDoneByRoutingOpId[rid] ?? 0);
    if (!Number.isFinite(qtyDone) || qtyDone <= 0) {
      alert("qty_done > 0 olmalı");
      return;
    }

    try {
      let startData = opStartDataByRoutingOpId[rid];
      if (!startData) {
        startData = await startProductionOrderOperation(orderId, rid);
        setOpStartDataByRoutingOpId((prev) => ({ ...prev, [rid]: startData }));
      }

      const base = Number(poOp.qty_target || order?.planned_qty || 0);
      const ratio = Number.isFinite(base) && base > 0 ? qtyDone / base : 1;

      const rawReq = Array.isArray(startData?.requirements) ? startData.requirements : [];
      const requirements = rawReq.map((r) => ({
        ...r,
        qty: Number(r.qty || 0) * ratio,
      }));

      const availableLots = Array.isArray(startData?.available_lots) ? startData.available_lots : [];
      setConsumptionInputs({});
      setManualConsumptionsByReqKey({});
      setFinishPanel({ rid, poOp, qtyDone, requirements, availableLots });
    } catch (err) {
      console.error("OP FINISH ERROR", err);
      const msg = err?.response?.data?.error?.message || err?.message || "Operasyon bitirilemedi";
      setOpActionError(msg);
      alert(msg);
    }
  }

  function buildConsumptionsFromInputs({ availableLots }) {
    const rows = [];
    for (const a of availableLots || []) {
      const matId = Number(a.material_id);
      const varId = a.material_variant_id === null || a.material_variant_id === undefined ? null : Number(a.material_variant_id);
      for (const l of a.lots || []) {
        const lotVarId =
          l?.material_variant_id === null || l?.material_variant_id === undefined
            ? varId
            : Number(l.material_variant_id);
        const key = buildLotKey({
          material_id: matId,
          material_variant_id: lotVarId,
          lot_no: l.lot_no,
        });
        const q = Number(consumptionInputs[key] || 0);
        if (!Number.isFinite(q) || q <= 0) continue;
        rows.push({
          material_id: matId,
          material_variant_id: lotVarId,
          lot_no: String(l.lot_no),
          qty: q,
        });
      }
    }

    // Add manual lot lines (operator may type a lot_no not listed)
    for (const [reqKey, lines] of Object.entries(manualConsumptionsByReqKey || {})) {
      const [matIdStr, varKeyStr] = String(reqKey).split(":");
      const matId = Number(matIdStr);
      const varKey = Number(varKeyStr);
      const varId = varKey === 0 ? null : varKey;
      for (const ln of lines || []) {
        const lotNo = normalizeLotNo(ln?.lot_no);
        const q = Number(ln?.qty);
        if (!lotNo) continue;
        if (!Number.isFinite(q) || q <= 0) continue;
        rows.push({
          material_id: matId,
          material_variant_id: varId,
          lot_no: lotNo,
          qty: q,
        });
      }
    }

    return mergeConsumptions(rows);
  }

  async function submitFinishPanel() {
    if (!finishPanel) return;
    const rid = Number(finishPanel.rid);
    const qtyDone = Number(finishPanel.qtyDone);
    const consumptions = buildConsumptionsFromInputs({ availableLots: finishPanel.availableLots });

    try {
      const payload = { qty_done: qtyDone, consumptions };
      const res = await finishProductionOrderOperation(orderId, rid, payload);
      if (res?.output_lot_no) {
        const suffix = res.output_variant_display_name
          ? ` (${res.output_variant_display_name})`
          : res.output_material_variant_id
            ? ` (Varyant ${res.output_material_variant_id})`
            : "";
        setOutputLotByRoutingOpId((prev) => ({ ...prev, [rid]: `${res.output_lot_no}${suffix}` }));
        alert(`Operasyon tamamlandı. Çıktı lot: ${res.output_lot_no}${suffix}`);
      } else {
        alert("Operasyon tamamlandı.");
      }
      setFinishPanel(null);
      setConsumptionInputs({});
      setManualConsumptionsByReqKey({});
      await load();
    } catch (err) {
      console.error("OP FINISH SUBMIT ERROR", err);
      const msg = err?.response?.data?.error?.message || err?.message || "Operasyon bitirilemedi";
      setOpActionError(msg);
      alert(msg);
    }
  }

  async function handleCompleteOp(poOp) {
    const rid = Number(poOp?.routing_operation_id);
    if (!Number.isFinite(rid) || !Number.isFinite(orderId)) return;

    const target = Math.max(0, Number(poOp?.qty_target || 0));
    const done = Math.max(0, Number(poOp?.qty_done || 0));
    const fireQtyNow = Math.max(0, target - done);
    const fireThreshold = target > 0 ? target * 0.01 : 0;
    const needsReason = fireQtyNow > fireThreshold;

    let fireReason = null;
    if (needsReason) {
      const pct = target > 0 ? ((fireQtyNow / target) * 100).toFixed(1) : "?";
      const reason = window.prompt(
        `Fire miktarı ${fireQtyNow.toLocaleString("tr-TR")} adet (hedefin %${pct}'i).\nLütfen fire nedenini açıklayın:`
      );
      if (reason == null) return; // iptal
      if (!reason.trim()) {
        alert("Fire nedeni boş bırakılamaz.");
        return;
      }
      fireReason = reason.trim();
    }

    const confirmMsg = needsReason
      ? `Fire: ${fireQtyNow.toLocaleString("tr-TR")} adet\nNeden: ${fireReason}\n\nOperasyonu komple bitirmek istediğinize emin misiniz?`
      : "Bu operasyonu komple bitirmek istediğinize emin misiniz?";
    const ok = window.confirm(confirmMsg);
    if (!ok) return;
    try {
      const res = await completeProductionOrderOperation(orderId, rid, fireReason);
      const msgParts = [];
      if (res?.warning) msgParts.push(String(res.warning));
      const fq = res?.fire_qty;
      if (fq != null && Number(fq) > 0) msgParts.push(`Fire: ${Number(fq)}`);
      const sk = res?.scrap_kg;
      if (sk != null && Number(sk) > 0) msgParts.push(`${Number(sk).toFixed(2)} kg hurda stoğa alındı`);
      if (msgParts.length) alert(msgParts.join(" | "));
      load();
    } catch (err) {
      const status = err?.response?.status;
      const rdata = err?.response?.data;
      const errCode = rdata?.error?.code;
      const message =
        rdata?.error?.message ||
        rdata?.message ||
        (typeof rdata === "string" ? rdata : null) ||
        err?.message ||
        null;
      if (status === 409 && errCode === "QC_PENDING") {
        alert(
          'Kalite onayı bekleniyor. Önce "Kalite Kontrol Onayları" ekranından QC onayı verin; ardından komple bitirebilirsiniz.'
        );
      } else {
        alert(message || "Komple bitirme başarısız");
      }
    }
  }

  async function submitOperationReport() {
    if (!Number.isFinite(orderId)) return;
    const routingOperationIdNum = Number(selectedRoutingOperationId);
    if (!Number.isFinite(routingOperationIdNum)) {
      alert("Operasyon seçin");
      return;
    }

    const setupMin = Number(opSetupMin);
    const machineMin = Number(opMachineMin);
    const laborMin = Number(opLaborMin);
    const qg = Number(opQtyGood);
    const qs = Number(opQtyScrap);

    if (!Number.isFinite(setupMin) || setupMin < 0) return alert("setup_min >= 0 olmalı");
    if (!Number.isFinite(machineMin) || machineMin < 0) return alert("machine_min >= 0 olmalı");
    if (!Number.isFinite(laborMin) || laborMin < 0) return alert("labor_min >= 0 olmalı");
    if (!Number.isFinite(qg) || qg < 0) return alert("qty_good >= 0 olmalı");
    if (!Number.isFinite(qs) || qs < 0) return alert("qty_scrap >= 0 olmalı");

    if (setupMin + machineMin + laborMin <= 0 && qg + qs <= 0) {
      alert("En az bir süre veya miktar alanı 0'dan büyük olmalı");
      return;
    }

    try {
      await createProductionOrderOperationReport(orderId, {
        routing_operation_id: routingOperationIdNum,
        setup_min: setupMin,
        machine_min: machineMin,
        labor_min: laborMin,
        qty_good: qg,
        qty_scrap: qs,
        note: opNote || null,
      });
      alert("Operasyon raporu kaydedildi.");
      await load();
    } catch (err) {
      console.error("OPERATION REPORT SUBMIT ERROR", err);
      alert("Operasyon raporu gönderilemedi");
    }
  }

  async function markCompleted() {
    if (!Number.isFinite(orderId)) return;
    if (!window.confirm("İş emrini tamamlamak istiyor musun?")) return;
    try {
      await completeProductionOrder(orderId);
      alert("İş emri tamamlandı");
      await load();
    } catch (err) {
      console.error("COMPLETE ORDER ERROR", err);
      alert("İş emri tamamlanamadı");
    }
  }

  if (!Number.isFinite(orderId)) {
    return (
      <div>
        <h2>İş Emri</h2>
        <div>Geçersiz id</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <h2>İş Emri Raporu</h2>
        <button onClick={() => navigate("/operator/assigned")}>← Listeye dön</button>
      </div>

      {loadingOrder ? (
        <div style={{ opacity: 0.8 }}>Yükleniyor...</div>
      ) : !order ? (
        <div>İş emri bulunamadı</div>
      ) : (
        <>
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div>
              <b>Emir No:</b> {order.order_no}
            </div>
            <div>
              <b>Ürün:</b> {order.product_name}
            </div>
            <div>
              <b>Planlanan:</b> {order.planned_qty} | <b>Durum:</b> {order.status}
            </div>
          </div>

          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Operasyonlar (Başlat / Bitir)</div>
            {opActionError ? (
              <div style={{ color: "#b00020", marginBottom: 8 }}>{opActionError}</div>
            ) : null}

            {order.production_order_operations?.length ? (
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Op</th>
                    <th>Durum</th>
                    <th style={{ textAlign: "right" }}>Hedef</th>
                    <th style={{ textAlign: "right" }}>Yapılan</th>
                    <th style={{ textAlign: "right" }}>Fire</th>
                    <th style={{ textAlign: "right" }}>qty_done</th>
                    <th>Çıktı Lot</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {order.production_order_operations.map((poOp) => {
                    const rid = Number(poOp.routing_operation_id);
                    const qtyDone = qtyDoneByRoutingOpId[rid] ?? "";
                    return (
                      <tr key={poOp.id}>
                        <td>
                          {poOp.op_no} - {poOp.operation_code}
                        </td>
                        <td>{poOp.status}</td>
                        <td style={{ textAlign: "right" }}>{Number(poOp.qty_target || 0).toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>{Number(poOp.qty_done || 0).toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>
                          {poOp.complete_fire_qty != null && poOp.complete_fire_qty !== ""
                            ? Number(poOp.complete_fire_qty)
                            : "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <input
                            type="number"
                            step="0.0001"
                            value={qtyDone}
                            onChange={(e) =>
                              setQtyDoneByRoutingOpId((prev) => ({
                                ...prev,
                                [rid]: e.target.value,
                              }))
                            }
                            style={{ width: 120 }}
                            disabled={String(poOp.status) !== "started"}
                          />
                        </td>
                        <td style={{ fontSize: 12 }}>{outputLotByRoutingOpId[rid] || ""}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <button onClick={() => startOp(poOp)} disabled={String(poOp.status) !== "planned"}>
                            Başlat
                          </button>{" "}
                          <button onClick={() => openFinishPanel(poOp)} disabled={String(poOp.status) !== "started"}>
                            Bitir
                          </button>{" "}
                          <button
                            onClick={() => handleCompleteOp(poOp)}
                            disabled={String(poOp.status) !== "started"}
                            style={{ backgroundColor: "#d32f2f", color: "#fff", border: "none", borderRadius: 4, padding: "4px 10px", cursor: String(poOp.status) === "started" ? "pointer" : "default" }}
                          >
                            Komple Bitti
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{ opacity: 0.8, fontSize: 12 }}>Operasyon bulunamadı.</div>
            )}

            {finishPanel ? (
              <div style={{ marginTop: 10, borderTop: "1px solid #eee", paddingTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <div style={{ fontWeight: 700 }}>
                    Bitir: Op {finishPanel.poOp?.op_no} ({finishPanel.poOp?.operation_code}) | qty_done:{" "}
                    {Number(finishPanel.qtyDone || 0).toFixed(4)}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFinishPanel(null);
                      setConsumptionInputs({});
                      setManualConsumptionsByReqKey({});
                    }}
                  >
                    Kapat
                  </button>
                </div>

                {(() => {
                  const al = finishPanel.availableLots || [];
                  const noLotsListed =
                    al.length === 0 || al.every((a) => !Array.isArray(a?.lots) || a.lots.length === 0);
                  if (!noLotsListed) return null;
                  return (
                    <div
                      style={{
                        marginTop: 10,
                        padding: 10,
                        background: "#f0f7ff",
                        borderRadius: 8,
                        fontSize: 13,
                        lineHeight: 1.45,
                      }}
                    >
                      Uygun lot listelenmiyor. Satış siparişine bağlı iş emirlerinde bazı malzemeler (ör. etiket, koli)
                      sipariş kalemiyle filtrelenir; stok ve giriş kayıtlarını kontrol edin. Gerekirse manuel lot
                      satırından giriş yapabilirsiniz (sunucu doğrulaması uygulanır).
                    </div>
                  );
                })()}

                {finishPanel.requirements?.length ? (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Gerekli komponentler (lot seç)</div>
                    {finishPanel.requirements.map((r) => {
                      const reqKey = buildReqKey(r);
                      const avail = (finishPanel.availableLots || []).find(
                        (a) =>
                          buildReqKey({
                            material_id: a.material_id,
                            material_variant_id: a.material_variant_id,
                          }) === reqKey
                      );
                      const lots = Array.isArray(avail?.lots) ? avail.lots : [];
                      const consumedSum = lots.reduce((acc, l) => {
                        const lotVarId =
                          l?.material_variant_id === null || l?.material_variant_id === undefined
                            ? r.material_variant_id
                            : Number(l.material_variant_id);
                        const k = buildLotKey({
                          material_id: r.material_id,
                          material_variant_id: lotVarId,
                          lot_no: l.lot_no,
                        });
                        const q = Number(consumptionInputs[k] || 0);
                        return acc + (Number.isFinite(q) ? q : 0);
                      }, 0);
                      const manualLines = Array.isArray(manualConsumptionsByReqKey[reqKey])
                        ? manualConsumptionsByReqKey[reqKey]
                        : [];
                      const manualSum = manualLines.reduce((acc, ln) => {
                        const q = Number(ln?.qty);
                        return acc + (Number.isFinite(q) ? q : 0);
                      }, 0);

                      return (
                        <div key={reqKey} style={{ border: "1px solid #eee", borderRadius: 10, padding: 10, marginBottom: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                            <div>
                              <b>Malzeme:</b> {r.material_id}
                              {r.material_variant_id ? ` (varyant ${r.material_variant_id})` : ""} | <b>Gerekli:</b>{" "}
                              {Number(r.qty || 0).toFixed(4)} {r.uom || ""}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.8 }}>
                              Seçilen: {Number(consumedSum || 0).toFixed(4)} | Manuel: {Number(manualSum || 0).toFixed(4)}
                            </div>
                          </div>

                          {lots.length ? (
                            <table style={{ width: "100%", marginTop: 8, fontSize: 12 }}>
                              <thead>
                                <tr>
                                  <th>Lot</th>
                                  <th>Varyant</th>
                                  <th style={{ textAlign: "right" }}>Stok</th>
                                  <th>Belge</th>
                                  <th>SKT</th>
                                  <th>Giriş</th>
                                  <th style={{ textAlign: "right" }}>Tüketim</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lots.map((l) => {
                                  const lotVarId =
                                    l?.material_variant_id === null || l?.material_variant_id === undefined
                                      ? r.material_variant_id
                                      : Number(l.material_variant_id);
                                  const k = buildLotKey({
                                    material_id: r.material_id,
                                    material_variant_id: lotVarId,
                                    lot_no: l.lot_no,
                                  });
                                  return (
                                    <tr key={k}>
                                      <td>{l.lot_no}</td>
                                      <td style={{ fontSize: 12 }}>
                                        {l.material_variant_display_name || (lotVarId ? `Varyant ${lotVarId}` : "")}
                                      </td>
                                      <td style={{ textAlign: "right" }}>{Number(l.on_hand_qty || 0).toFixed(4)}</td>
                                      <td>{l.inbound_doc_no || ""}</td>
                                      <td>{l.expiry_date || ""}</td>
                                      <td>{l.received_at || ""}</td>
                                      <td style={{ textAlign: "right" }}>
                                        <input
                                          type="number"
                                          step="0.0001"
                                          value={consumptionInputs[k] ?? ""}
                                          onChange={(e) =>
                                            setConsumptionInputs((prev) => ({
                                              ...prev,
                                              [k]: e.target.value,
                                            }))
                                          }
                                          style={{ width: 120 }}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>Bu komponent için lot bulunamadı.</div>
                          )}

                          <div style={{ marginTop: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                              <div style={{ fontWeight: 700, fontSize: 12 }}>Manuel lot no ile tüketim</div>
                              <button
                                type="button"
                                onClick={() => {
                                  const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                                  setManualConsumptionsByReqKey((prev) => ({
                                    ...prev,
                                    [reqKey]: [...(Array.isArray(prev[reqKey]) ? prev[reqKey] : []), { id, lot_no: "", qty: "" }],
                                  }));
                                }}
                              >
                                + Satır ekle
                              </button>
                            </div>

                            {manualLines.length ? (
                              <table style={{ width: "100%", marginTop: 6, fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: "left" }}>Lot No</th>
                                    <th style={{ textAlign: "right" }}>Tüketim</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {manualLines.map((ln, idx) => {
                                    const rowId = ln?.id || `${reqKey}-${idx}`;
                                    return (
                                      <tr key={rowId}>
                                        <td>
                                          <input
                                            value={ln?.lot_no ?? ""}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setManualConsumptionsByReqKey((prev) => ({
                                                ...prev,
                                                [reqKey]: (Array.isArray(prev[reqKey]) ? prev[reqKey] : []).map((x) =>
                                                  (x?.id || "") === (ln?.id || "") ? { ...x, lot_no: v } : x
                                                ),
                                              }));
                                            }}
                                            placeholder="LOT-001"
                                            style={{ width: "100%" }}
                                          />
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                          <input
                                            type="number"
                                            step="0.0001"
                                            value={ln?.qty ?? ""}
                                            onChange={(e) => {
                                              const v = e.target.value;
                                              setManualConsumptionsByReqKey((prev) => ({
                                                ...prev,
                                                [reqKey]: (Array.isArray(prev[reqKey]) ? prev[reqKey] : []).map((x) =>
                                                  (x?.id || "") === (ln?.id || "") ? { ...x, qty: v } : x
                                                ),
                                              }));
                                            }}
                                            style={{ width: 120 }}
                                          />
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setManualConsumptionsByReqKey((prev) => ({
                                                ...prev,
                                                [reqKey]: (Array.isArray(prev[reqKey]) ? prev[reqKey] : []).filter(
                                                  (x) => (x?.id || "") !== (ln?.id || "")
                                                ),
                                              }));
                                            }}
                                          >
                                            Sil
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            ) : (
                              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                                Lot listede yoksa veya hızlı giriş için buradan lot no yazabilirsiniz.
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>Bu operasyon için tüketim gerekmiyor.</div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button type="button" onClick={submitFinishPanel}>
                    Bitir (stok hareketi + çıktı)
                  </button>
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
              Not: Stok hareketi operasyon <b>Bitir</b> ile yapılır. Tüketimde lot seçimi yapılır (multi-lot).
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Bu üretimde</div>
              <label style={{ display: "block", marginBottom: 8 }}>
                İyi miktar (qty_good)
                <input
                  type="number"
                  step="0.0001"
                  value={qtyGood}
                  onChange={(e) => setQtyGood(e.target.value)}
                  style={{ width: "100%" }}
                />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                Fire (qty_scrap)
                <input
                  type="number"
                  step="0.0001"
                  value={qtyScrap}
                  onChange={(e) => setQtyScrap(e.target.value)}
                  style={{ width: "100%" }}
                />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                Not (opsiyonel)
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  style={{ width: "100%" }}
                  placeholder="..."
                />
              </label>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={submitReport}>Rapor Gönder</button>
                <button onClick={markCompleted} disabled={order.status === "completed"}>
                  Tamamla
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.75 }}>
                Not: Bu rapor sadece kayıt içindir; stok hareketi operasyon Bitir ile yapılır.
              </div>
            </div>

            <div />
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Operasyon Raporu</div>
            {order.routing_operations?.length ? (
              <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <label style={{ display: "block" }}>
                    Operasyon
                    <select
                      value={selectedRoutingOperationId}
                      onChange={(e) => setSelectedRoutingOperationId(e.target.value)}
                      style={{ width: "100%" }}
                    >
                      {order.routing_operations.map((op) => (
                        <option key={op.id} value={op.id}>
                          {op.op_no} - {op.operation_code} ({op.work_center_name || op.work_center_code || "İş Merkezi"})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: "block" }}>
                    Not (opsiyonel)
                    <input
                      value={opNote}
                      onChange={(e) => setOpNote(e.target.value)}
                      style={{ width: "100%" }}
                      placeholder="..."
                    />
                  </label>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 12, marginTop: 10 }}>
                  <label style={{ display: "block" }}>
                    Setup (dk)
                    <input
                      type="number"
                      step="0.0001"
                      value={opSetupMin}
                      onChange={(e) => setOpSetupMin(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label style={{ display: "block" }}>
                    Makine (dk)
                    <input
                      type="number"
                      step="0.0001"
                      value={opMachineMin}
                      onChange={(e) => setOpMachineMin(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label style={{ display: "block" }}>
                    İşçilik (dk)
                    <input
                      type="number"
                      step="0.0001"
                      value={opLaborMin}
                      onChange={(e) => setOpLaborMin(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label style={{ display: "block" }}>
                    İyi (ops.)
                    <input
                      type="number"
                      step="0.0001"
                      value={opQtyGood}
                      onChange={(e) => setOpQtyGood(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label style={{ display: "block" }}>
                    Fire (ops.)
                    <input
                      type="number"
                      step="0.0001"
                      value={opQtyScrap}
                      onChange={(e) => setOpQtyScrap(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>

                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={submitOperationReport}>Operasyon Raporu Gönder</button>
                </div>
              </div>
            ) : (
              <div style={{ opacity: 0.8, fontSize: 12 }}>
                Bu iş emri için rota/operasyon bulunamadı.
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Geçmiş operasyon raporları</div>
              {order.operation_reports?.length ? (
                <table style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Op</th>
                      <th>İş Merkezi</th>
                      <th style={{ textAlign: "right" }}>Setup</th>
                      <th style={{ textAlign: "right" }}>Makine</th>
                      <th style={{ textAlign: "right" }}>İşçilik</th>
                      <th style={{ textAlign: "right" }}>İyi</th>
                      <th style={{ textAlign: "right" }}>Fire</th>
                      <th>Not</th>
                      <th>Zaman</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.operation_reports.map((r) => (
                      <tr key={r.id}>
                        <td>{r.id}</td>
                        <td>
                          {r.op_no} - {r.operation_code}
                        </td>
                        <td>{r.work_center_name || r.work_center_code}</td>
                        <td style={{ textAlign: "right" }}>{Number(r.setup_min || 0).toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>{Number(r.machine_min || 0).toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>{Number(r.labor_min || 0).toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>{Number(r.qty_good || 0).toFixed(2)}</td>
                        <td style={{ textAlign: "right" }}>{Number(r.qty_scrap || 0).toFixed(2)}</td>
                        <td>{r.note}</td>
                        <td>{r.reported_at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ opacity: 0.8, fontSize: 12 }}>Henüz operasyon raporu yok.</div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Geçmiş raporlar</div>
            {order.reports?.length ? (
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th style={{ textAlign: "right" }}>İyi</th>
                    <th style={{ textAlign: "right" }}>Fire</th>
                    <th>Not</th>
                    <th>Zaman</th>
                  </tr>
                </thead>
                <tbody>
                  {order.reports.map((r) => (
                    <tr key={r.id}>
                      <td>{r.id}</td>
                      <td style={{ textAlign: "right" }}>{r.qty_good}</td>
                      <td style={{ textAlign: "right" }}>{r.qty_scrap}</td>
                      <td>{r.note}</td>
                      <td>{r.reported_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ opacity: 0.8, fontSize: 12 }}>Henüz rapor yok.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
